import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useQuery} from '@github-ui/react-query'
import {Link, useLocation} from '@github-ui/react-router'
import {getIconColorClassName, getIconComponent} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {PlusIcon, StackIcon} from '@primer/octicons-react'
import {ActionList, NavList} from '@primer/react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {savedViewsQueryOptions} from '../../queries/saved-views'
import type {SavedView} from '../views/types'

export type RepoSavedViewsNavProps = {
  owner: string
  repo: string
  /** Path to the saved-views index page (`/{owner}/{repo}/issues/views`). */
  viewsPath: string
  /** Whether the current route is within the views section. */
  isCurrent: boolean
  /** Force a full page load instead of soft navigation. */
  forceHardNavigation: boolean
  onClick?: () => void
}

/**
 * Repo issues sidebar "Views" navigation.
 *
 * - Expanded sidebar: a plain "Views" link to the saved-views index page
 *   (unchanged from the previous behavior).
 * - Collapsed rail: a collapsible group whose icon trigger opens a popover
 *   listing the viewer's saved views, a link to the full index, and — for
 *   users who can manage views — a "New view" action. This mirrors the
 *   dashboard saved-views pattern so views are manageable without expanding
 *   the sidebar.
 */
export function RepoSavedViewsNav({
  owner,
  repo,
  viewsPath,
  isCurrent,
  forceHardNavigation,
  onClick,
}: RepoSavedViewsNavProps) {
  const {isExpanded} = useCollapsibleSidebarContext()

  if (isExpanded) {
    return (
      <CollapsibleNavList.LinkItem
        to={viewsPath}
        icon={StackIcon}
        label={LABELS.viewsTitle}
        forceHardNavigation={forceHardNavigation}
        aria-current={isCurrent ? 'page' : undefined}
        onClick={onClick}
      />
    )
  }

  return (
    <CollapsedSavedViewsGroup
      owner={owner}
      repo={repo}
      viewsPath={viewsPath}
      isCurrent={isCurrent}
      forceHardNavigation={forceHardNavigation}
      onClick={onClick}
    />
  )
}

function CollapsedSavedViewsGroup({
  owner,
  repo,
  viewsPath,
  isCurrent,
  forceHardNavigation,
  onClick,
}: RepoSavedViewsNavProps) {
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const {data} = useQuery(savedViewsQueryOptions({owner, repo}))
  const savedViews = data?.savedViews ?? []
  const viewerCanManageViews = data?.viewerCanManageViews ?? false
  const newViewUrl = `${viewsPath}/new`

  // `CollapsibleNavList.Group` marks the collapsed trigger as current only when a
  // direct child carries `aria-current`, so flag the matching row. "All views"
  // stands in whenever we're in the views section without a specific view selected
  // (index, /new, an unlisted id) to match the expanded link's highlighting.
  const currentViewId = savedViews.find(view => pathname === `${viewsPath}/${view.id}`)?.id
  const isAllViewsCurrent = isCurrent && currentViewId === undefined

  return (
    <CollapsibleNavList.Group
      icon={StackIcon}
      title={LABELS.viewsTitle}
      trailingAction={
        viewerCanManageViews ? (
          <ActionList.GroupHeading.TrailingAction
            as="button"
            icon={PlusIcon}
            label={BUTTON_LABELS.newView}
            onClick={() => {
              // Honor `forceHardNavigation` like the saved-view/all-view links:
              // hosts that mount this sidebar outside the issues router (e.g. the
              // Rails partial) need a full navigation, not a router soft nav.
              if (forceHardNavigation) {
                window.location.assign(newViewUrl)
              } else {
                navigate(newViewUrl)
              }
            }}
            tooltipDirection="e"
          />
        ) : undefined
      }
    >
      {savedViews.map(view => (
        <SavedViewNavItem
          key={view.id}
          view={view}
          viewsPath={viewsPath}
          aria-current={view.id === currentViewId ? 'page' : undefined}
          forceHardNavigation={forceHardNavigation}
          onClick={onClick}
        />
      ))}
      <AllViewsNavItem
        viewsPath={viewsPath}
        aria-current={isAllViewsCurrent ? 'page' : undefined}
        forceHardNavigation={forceHardNavigation}
        onClick={onClick}
      />
    </CollapsibleNavList.Group>
  )
}

function SavedViewNavItem({
  view,
  viewsPath,
  'aria-current': ariaCurrent,
  forceHardNavigation,
  onClick,
}: {
  view: SavedView
  viewsPath: string
  'aria-current'?: 'page'
  forceHardNavigation: boolean
  onClick?: () => void
}) {
  const to = `${viewsPath}/${view.id}`
  // Guard against an empty name so the link always has a discernible accessible
  // name (the leading visual icon is decorative / aria-hidden).
  const name = view.name.trim() || LABELS.views.defaultName

  if (forceHardNavigation) {
    return (
      <NavList.Item href={to} aria-current={ariaCurrent} onClick={onClick}>
        <NavList.LeadingVisual>
          <SavedViewIcon icon={view.icon} color={view.color} />
        </NavList.LeadingVisual>
        {name}
      </NavList.Item>
    )
  }

  return (
    <NavList.Item as={Link} to={to} aria-current={ariaCurrent} onClick={onClick}>
      <NavList.LeadingVisual>
        <SavedViewIcon icon={view.icon} color={view.color} />
      </NavList.LeadingVisual>
      {name}
    </NavList.Item>
  )
}

function AllViewsNavItem({
  viewsPath,
  'aria-current': ariaCurrent,
  forceHardNavigation,
  onClick,
}: {
  viewsPath: string
  'aria-current'?: 'page'
  forceHardNavigation: boolean
  onClick?: () => void
}) {
  if (forceHardNavigation) {
    return (
      <NavList.Item href={viewsPath} aria-current={ariaCurrent} onClick={onClick}>
        <NavList.LeadingVisual>
          <StackIcon />
        </NavList.LeadingVisual>
        {LABELS.allViews}
      </NavList.Item>
    )
  }

  return (
    <NavList.Item as={Link} to={viewsPath} aria-current={ariaCurrent} onClick={onClick}>
      <NavList.LeadingVisual>
        <StackIcon />
      </NavList.LeadingVisual>
      {LABELS.allViews}
    </NavList.Item>
  )
}

function SavedViewIcon({icon, color}: {icon: string; color: string}) {
  const IconComponent = getIconComponent(icon)
  return (
    <span className={getIconColorClassName(color)}>
      {/* eslint-disable-next-line react-hooks/static-components -- getIconComponent selects a static octicon; it is not a dynamically-created component */}
      <IconComponent />
    </span>
  )
}
